import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, UserCheck, X, Loader2, StickyNote, Calendar, Receipt, Ticket, CreditCard } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import DepositCardModal from "@/components/pos/DepositCardModal";
import RedeemPackageModal from "@/pages/pos/RedeemPackageModal";
import { fmtDate, fmtTimeRange, money } from "@/lib/format";

const NOTE_CATS = ["session", "front_desk", "follow_up", "preference", "recommendation", "admin", "cancellation", "no_show", "incident", "membership"];

function BirthdayField({ client }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(client.dob || "");
  const save = async () => {
    try {
      const { id, created_at, updated_at, checked_in, checked_in_at, created_by, store_credit, stripe_customer_id, ...rest } = client;
      await api.put(`/pos/clients/${client.id}`, { ...rest, dob: val });
      toast.success("Birthday saved");
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["pos-client", client.id] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!editing) {
    return (
      <p className="text-xs text-[#8FA89B] mt-0.5" data-testid="client-dob-display">
        🎂 {client.dob || "No birthday on file"}
        <button onClick={() => setEditing(true)} data-testid="client-dob-edit" className="ml-2 text-[#144F75] underline">{client.dob ? "Edit" : "Add"}</button>
      </p>
    );
  }
  return (
    <span className="inline-flex items-center gap-2 mt-1">
      <input type="date" value={val} onChange={(e) => setVal(e.target.value)} data-testid="client-dob-input" className="eyw-input !py-1 text-xs" />
      <button onClick={save} data-testid="client-dob-save" className="text-xs text-[#144F75] underline">Save</button>
      <button onClick={() => setEditing(false)} className="text-xs text-[#8FA89B]">Cancel</button>
    </span>
  );
}

function CreateClientModal({ onClose, onSaved }) {
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", dob: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/pos/clients", f);
      toast.success("Client created");
      onSaved(data);
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center px-4 bg-black/40 backdrop-blur-sm" data-testid="client-create-modal">
      <form onSubmit={submit} className="relative w-full max-w-md bg-[#F9F7F3] rounded-3xl shadow-2xl p-8">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[#4A5D6B]"><X size={20} /></button>
        <h3 className="font-serif text-2xl text-[#1A242B] mb-4">New client</h3>
        <div className="space-y-3">
          <input required placeholder="First name" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} data-testid="client-first" className="eyw-input w-full" />
          <input required placeholder="Last name" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} data-testid="client-last" className="eyw-input w-full" />
          <input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="client-email" className="eyw-input w-full" />
          <input placeholder="Phone (optional)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="client-phone" className="eyw-input w-full" />
          <div>
            <label className="text-xs text-[#4A5D6B]">Birthday (optional — unlocks birthday-week perks)</label>
            <input type="date" value={f.dob} onChange={(e) => setF({ ...f, dob: e.target.value })} data-testid="client-dob" className="eyw-input w-full" />
          </div>
        </div>
        <button type="submit" disabled={loading} data-testid="client-save" className="eyw-btn w-full mt-5 disabled:opacity-60">{loading && <Loader2 className="animate-spin" size={18} />} Create client</button>
      </form>
    </div>
  );
}

function ClientProfile({ id, onBack }) {
  const qc = useQueryClient();
  const [note, setNote] = useState({ category: "front_desk", body: "" });
  const [redeemCredit, setRedeemCredit] = useState(null);
  const [showAllVisits, setShowAllVisits] = useState(false);
  const [showAllSales, setShowAllSales] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["pos-client", id], queryFn: () => api.get(`/pos/clients/${id}`).then((r) => r.data) });

  const addNote = async () => {
    if (!note.body) return;
    try {
      await api.post(`/pos/clients/${id}/notes`, note);
      setNote({ category: "front_desk", body: "" });
      qc.invalidateQueries({ queryKey: ["pos-client", id] });
      toast.success("Note added");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const checkin = async () => {
    await api.post(`/pos/clients/${id}/checkin`, {});
    qc.invalidateQueries({ queryKey: ["pos-client", id] });
    toast.success("Checked in");
  };

  if (isLoading || !data) return <div className="py-16 text-center text-[#4A5D6B]"><Loader2 className="animate-spin inline" /></div>;
  const c = data.client;
  const todayStr = new Date().toISOString().slice(0, 10);
  const pastAppts = data.appointments.filter((a) => a.date < todayStr && a.status !== "cancelled");
  const upcomingAppts = data.appointments.filter((a) => a.date >= todayStr && a.status !== "cancelled").sort((x, y) => (x.date + x.start_time).localeCompare(y.date + y.start_time));
  const paidSales = data.sales.filter((s) => ["paid", "partial"].includes(s.payment_status));
  const totalSpend = paidSales.reduce((sum, s) => sum + (s.total || 0), 0);
  const lastVisit = pastAppts[0]?.date || "";
  return (
    <div data-testid="client-profile">
      <button onClick={onBack} className="text-sm text-[#144F75] mb-4">← Back to clients</button>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="font-serif text-3xl text-[#1A242B]">{c.first_name} {c.last_name}</h2>
          <p className="text-sm text-[#4A5D6B]">{c.email} {c.phone && `· ${c.phone}`}</p>
          <BirthdayField client={c} />
          {c.alerts?.length > 0 && <p className="text-xs text-[#B5794E] mt-1">⚠ {c.alerts.join(", ")}</p>}
        </div>
        <button onClick={checkin} data-testid="client-checkin" className={`eyw-btn !py-2 text-sm ${c.checked_in ? "opacity-60" : ""}`}><UserCheck size={16} /> {c.checked_in ? "Checked in" : "Check in"}</button>
      </div>

      {data.birthday_week && (
        <div className="rounded-xl bg-[#D98E73]/10 border border-[#D98E73]/30 px-4 py-3 mb-4 text-sm text-[#8A5A3B]" data-testid="client-birthday-banner">
          🎂 It's {c.first_name}'s birthday week — greet them warmly! Any birthday perk your center offers applies automatically at the register.
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="client-visit-summary">
        {[
          ["Past visits", String(pastAppts.length)],
          ["Last visit", lastVisit || "First visit"],
          ["Total spend", `${money(totalSpend)}`],
          ["Client since", (c.created_at || "").slice(0, 10) || "—"],
        ].map(([label, value]) => (
          <div key={label} className="eyw-card px-4 py-3">
            <p className="text-[10px] uppercase tracking-wide text-[#8FA89B]">{label}</p>
            <p className="text-sm font-medium text-[#1A242B] mt-0.5">{value}</p>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="eyw-card p-6" data-testid="client-visit-history">
          <h3 className="font-serif text-lg text-[#1A242B] mb-3 flex items-center gap-2"><Calendar size={17} /> Visit history</h3>
          {upcomingAppts.length > 0 && (
            <div className="mb-3" data-testid="client-upcoming-appts">
              <p className="text-[10px] uppercase tracking-wide text-[#5C8A72] mb-1.5">Upcoming</p>
              <ul className="space-y-2">{upcomingAppts.slice(0, 3).map((a) => (
                <li key={a.id} className="text-sm flex justify-between gap-2 rounded-lg bg-[#5C8A72]/[0.07] px-2.5 py-1.5">
                  <span className="text-[#1A242B]">{a.service_title}{a.enhancer_name ? <span className="text-[#8FA89B]"> · {a.enhancer_name}</span> : null}</span>
                  <span className="text-xs text-[#5C8A72] shrink-0">{fmtDate(a.date)} {fmtTimeRange(a.start_time)}</span>
                </li>
              ))}</ul>
            </div>
          )}
          {pastAppts.length ? (
            <>
              {upcomingAppts.length > 0 && <p className="text-[10px] uppercase tracking-wide text-[#8FA89B] mb-1.5">Past</p>}
              <ul className="space-y-2" data-testid="client-past-appts">{(showAllVisits ? pastAppts : pastAppts.slice(0, 8)).map((a) => (
                <li key={a.id} className="text-sm flex justify-between gap-2">
                  <span className="text-[#1A242B]">{a.service_title}{a.enhancer_name ? <span className="text-[#8FA89B]"> · {a.enhancer_name}</span> : null}</span>
                  <span className="text-xs text-[#4A5D6B] shrink-0">{fmtDate(a.date)}{a.start_time ? ` ${fmtTimeRange(a.start_time)}` : ""} · {a.status}</span>
                </li>
              ))}</ul>
              {pastAppts.length > 8 && (
                <button onClick={() => setShowAllVisits(!showAllVisits)} data-testid="client-visits-more" className="text-xs text-[#144F75] mt-2.5">
                  {showAllVisits ? "Show fewer" : `Show all ${pastAppts.length} visits`}
                </button>
              )}
            </>
          ) : !upcomingAppts.length ? <p className="text-sm text-[#4A5D6B]">No visits yet — this is their first time.</p>
            : <p className="text-sm text-[#4A5D6B]">No past visits — first appointment coming up.</p>}
        </div>
        <div className="eyw-card p-6" data-testid="client-purchases">
          <h3 className="font-serif text-lg text-[#1A242B] mb-3 flex items-center gap-2"><Receipt size={17} /> Purchases</h3>
          {data.sales.length ? (
            <>
              <ul className="space-y-2.5">{(showAllSales ? data.sales : data.sales.slice(0, 8)).map((s) => {
                const titles = (s.items || []).map((i) => i.title).filter(Boolean);
                return (
                  <li key={s.id} className="text-sm" data-testid={`client-sale-${s.id}`}>
                    <div className="flex justify-between gap-2">
                      <span className="text-[#1A242B]">{titles.length ? (titles.slice(0, 2).join(", ") + (titles.length > 2 ? ` +${titles.length - 2} more` : "")) : `${s.items?.length || 0} items`}</span>
                      <span className="text-xs text-[#144F75] shrink-0">{money((s.total || 0))}</span>
                    </div>
                    <p className="text-[10px] text-[#8FA89B]">{(s.created_at || "").slice(0, 10)} · {s.payment_status || s.status}{s.discount_total > 0 ? ` · saved ${money(s.discount_total)}` : ""}</p>
                  </li>
                );
              })}</ul>
              {data.sales.length > 8 && (
                <button onClick={() => setShowAllSales(!showAllSales)} data-testid="client-sales-more" className="text-xs text-[#144F75] mt-2.5">
                  {showAllSales ? "Show fewer" : `Show all ${data.sales.length} purchases`}
                </button>
              )}
            </>
          ) : <p className="text-sm text-[#4A5D6B]">No purchases yet.</p>}
        </div>
        <div className="eyw-card p-6">
          <h3 className="font-serif text-lg text-[#1A242B] mb-3 flex items-center gap-2"><Ticket size={17} /> Package credits</h3>
          {data.credits.length ? <ul className="space-y-2">{data.credits.map((cr) => {
            const expired = cr.expires_at && String(cr.expires_at).slice(0, 10) < new Date().toISOString().slice(0, 10);
            return (
              <li key={cr.id} className="text-sm">
                <div className="flex justify-between items-center gap-2">
                  <span className="text-[#1A242B]">{cr.package_name}
                    {cr.expires_at && <span className={`block text-[10px] ${expired ? "text-red-500" : "text-[#8FA89B]"}`}>{expired ? "expired" : "expires"} {String(cr.expires_at).slice(0, 10)}</span>}
                  </span>
                  <span className="text-xs text-[#5C8A72] shrink-0">{cr.sessions_remaining}/{cr.sessions_total} left</span>
                </div>
                {cr.sessions_remaining > 0 && !expired && (
                  <button onClick={() => setRedeemCredit(cr)} data-testid={`redeem-credit-${cr.id}`} className="eyw-btn-ghost !py-1 !px-3 text-xs mt-1.5">
                    <Ticket size={12} /> Redeem session
                  </button>
                )}
              </li>
            );
          })}</ul> : <p className="text-sm text-[#4A5D6B]">No active packages.</p>}
        </div>
        <MemberCustomFields client={c} onSaved={() => qc.invalidateQueries({ queryKey: ["pos-client", id] })} />
        {redeemCredit && <RedeemPackageModal credit={redeemCredit} clientId={id} onClose={() => setRedeemCredit(null)} />}
        <div className="eyw-card p-6">
          <h3 className="font-serif text-lg text-[#1A242B] mb-3 flex items-center gap-2"><StickyNote size={17} /> Notes</h3>
          <div className="flex gap-2 mb-3">
            <select value={note.category} onChange={(e) => setNote({ ...note, category: e.target.value })} data-testid="note-category" className="eyw-input !py-1.5 text-xs">
              {NOTE_CATS.map((n) => <option key={n} value={n}>{n.replace("_", " ")}</option>)}
            </select>
          </div>
          <textarea placeholder="Add a note…" rows={2} maxLength={2000} value={note.body} onChange={(e) => setNote({ ...note, body: e.target.value })} data-testid="note-body" className="eyw-input w-full resize-none text-sm" />
          <button onClick={addNote} data-testid="note-add" className="eyw-btn-ghost !py-1.5 text-xs mt-2">Add note</button>
          <ul className="space-y-2 mt-4 max-h-52 overflow-y-auto">
            {data.notes.map((n) => (
              <li key={n.id} className="text-sm border-b border-[#E5E0D8] pb-2 last:border-0">
                <span className="text-[11px] uppercase tracking-wide text-[#144F75]">{n.category}</span>
                <p className="text-[#4A5D6B]">{n.body}</p>
                <span className="text-[10px] text-[#8FA89B]">{n.author_name} · {n.created_at?.slice(0, 10)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function MemberCustomFields({ client, onSaved }) {
  const [values, setValues] = useState(client.custom_fields || {});
  const [saving, setSaving] = useState(false);
  const { data: fields = [] } = useQuery({
    queryKey: ["cf-member"],
    queryFn: () => api.get("/custom-fields?entity=member").then((r) => r.data),
  });
  if (fields.length === 0) return null;

  const save = async (next) => {
    setValues(next);
    setSaving(true);
    try {
      const { first_name, last_name, email, phone, dob, address,
        emergency_contact_name, emergency_contact_phone, tags, alerts } = client;
      await api.put(`/pos/clients/${client.id}`, {
        first_name, last_name: last_name || "", email: email || "", phone: phone || "",
        dob: dob || "", address: address || "",
        emergency_contact_name: emergency_contact_name || "", emergency_contact_phone: emergency_contact_phone || "",
        tags: tags || [], alerts: alerts || [], custom_fields: next,
      });
      onSaved && onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="eyw-card p-6" data-testid="member-custom-fields">
      <h3 className="font-serif text-lg text-[#1A242B] mb-3">Profile fields {saving && <Loader2 className="animate-spin inline ml-1" size={13} />}</h3>
      <div className="space-y-3">
        {fields.map((f) => (
          <div key={f.id}>
            <label className="text-xs text-[#4A5D6B] mb-1 block">{f.label}</label>
            <select
              value={values[f.key] || ""}
              onChange={(e) => save({ ...values, [f.key]: e.target.value })}
              data-testid={`member-cf-${f.key}`}
              className="eyw-input w-full !py-2 text-sm"
            >
              <option value="">—</option>
              {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}

function IntakeModal({ onClose, onSaved }) {
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", service_id: "", date: "", start_time: "10:00", deposit_method: "card", deposit_amount: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showCard, setShowCard] = useState(false);
  const [savedCards, setSavedCards] = useState([]);
  const [pmId, setPmId] = useState("");
  const [saveCard, setSaveCard] = useState(true);
  const [agSel, setAgSel] = useState({});
  const { data: services = [] } = useQuery({ queryKey: ["intake-services"], queryFn: () => api.get("/pos/services").then((r) => r.data) });
  const { data: agreements = [] } = useQuery({ queryKey: ["intake-agreements"], queryFn: () => api.get("/agreements").then((r) => r.data) });

  const isRequired = (a) => ["member", "pma"].includes(a.type) || (a.required_for || []).some((r) => ["membership", "booking"].includes(r));
  const svc = services.find((s) => s.id === f.service_id);
  const suggested = svc ? Math.round(((svc.price || 0) * (svc.deposit_pct || 25) / 100) * 100) / 100 : 0;

  const toggleAg = (id) => setAgSel((p) => ({ ...p, [id]: !(id in p ? p[id] : true) }));
  const isChecked = (a) => (a.id in agSel ? agSel[a.id] : isRequired(a));

  const lookupCards = async () => {
    const email = f.email.trim().toLowerCase();
    if (!email || !email.includes("@")) return;
    try {
      const { data } = await api.get(`/pos/clients?q=${encodeURIComponent(email)}`);
      const match = (data || []).find((c) => (c.email || "").toLowerCase() === email);
      if (!match) { setSavedCards([]); return; }
      const cards = await api.get(`/pos/clients/${match.id}/cards`).then((r) => r.data.cards || []);
      setSavedCards(cards);
      if (cards.length) setPmId(cards[0].id);
    } catch { setSavedCards([]); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const agreement_ids = agreements.filter((a) => isChecked(a)).map((a) => a.id);
      const body = { ...f, deposit_amount: f.deposit_amount === "" ? 0 : Number(f.deposit_amount), agreement_ids, save_card: saveCard, payment_method_id: pmId };
      const { data } = await api.post("/pos/intake", body);
      setResult(data);
      if (data.client_secret) setShowCard(true);
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center px-4 bg-black/40 backdrop-blur-sm overflow-y-auto py-8" data-testid="intake-modal">
      <div className="relative w-full max-w-lg bg-[#F9F7F3] rounded-3xl shadow-2xl p-8 my-auto">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[#4A5D6B]"><X size={20} /></button>
        {result ? (
          <div data-testid="intake-result">
            <h3 className="font-serif text-2xl text-[#1A242B] mb-2 flex items-center gap-2"><UserCheck size={22} className="text-[#5C8A72]" /> Member added</h3>
            <p className="text-sm text-[#4A5D6B] mb-4">Appointment reserved. {result.email_sent ? "A confirm-and-sign email was sent to the member." : "Email could not be sent — share the link below."}</p>
            <div className="eyw-card p-4 space-y-2 text-sm">
              <p><b>Deposit:</b> {money(Number(result.deposit?.amount || 0))} · {result.deposit?.method} · <span className="capitalize">{String(result.deposit?.status || "").replace(/_/g, " ")}</span></p>
              {result.deposit?.method === "card" && result.deposit?.status === "pending" && result.client_secret && (
                <button onClick={() => setShowCard(true)} data-testid="intake-enter-card" className="eyw-btn !py-1.5 text-xs"><CreditCard size={13} /> Enter card &amp; charge deposit</button>
              )}
              {result.deposit?.status === "pay_in_person" && (
                <p className="text-xs text-[#B5794E]">Deposit will be collected in person at the appointment.</p>
              )}
              {result.deposit?.method === "card" && result.deposit?.status === "unconfigured" && (
                <p className="text-xs text-[#B5794E]">Card payments aren't set up yet — collect this deposit another way.</p>
              )}
              <div className="pt-2 border-t border-[#E5E0D8]">
                <p className="text-xs text-[#8FA89B] mb-1">Confirm &amp; sign link:</p>
                <div className="flex items-center gap-2">
                  <input readOnly value={result.confirm_link} data-testid="intake-confirm-link" className="eyw-input flex-1 text-xs" />
                  <button onClick={() => { navigator.clipboard.writeText(result.confirm_link); toast.success("Copied"); }} className="eyw-btn !py-1.5 !px-3 text-xs">Copy</button>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="eyw-btn w-full mt-5">Done</button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h3 className="font-serif text-2xl text-[#1A242B] mb-1">New Member + Appointment</h3>
            <p className="text-sm text-[#4A5D6B] mb-4">Adds a Discovery Member, takes a deposit, and emails their agreements to confirm.</p>
            <div className="grid grid-cols-2 gap-3">
              <input required placeholder="First name" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} data-testid="intake-first" className="eyw-input w-full" />
              <input required placeholder="Last name" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} data-testid="intake-last" className="eyw-input w-full" />
              <input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} onBlur={lookupCards} data-testid="intake-email" className="eyw-input w-full" />
              <input placeholder="Phone (optional)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="intake-phone" className="eyw-input w-full" />
            </div>
            <select required value={f.service_id} onChange={(e) => setF({ ...f, service_id: e.target.value })} data-testid="intake-service" className="eyw-input w-full mt-3">
              <option value="">Select a service…</option>
              {services.map((s) => <option key={s.id} value={s.id}>{s.title} — {money(s.price)}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <input required type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="intake-date" className="eyw-input w-full" />
              <input required type="time" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} data-testid="intake-time" className="eyw-input w-full" />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <select value={f.deposit_method} onChange={(e) => setF({ ...f, deposit_method: e.target.value })} data-testid="intake-deposit-method" className="eyw-input w-full">
                <option value="card">Card now</option>
                <option value="cash">Cash now</option>
                <option value="in_person">Pay in person</option>
                <option value="none">No deposit</option>
              </select>
              <input type="number" min="0" step="0.01" placeholder={suggested ? `Deposit ${money(suggested)}` : "Deposit amount"} value={f.deposit_amount} onChange={(e) => setF({ ...f, deposit_amount: e.target.value })} data-testid="intake-deposit-amount" className="eyw-input w-full" disabled={f.deposit_method === "none"} />
            </div>
            {f.deposit_method === "card" && (
              <div className="mt-3 space-y-2" data-testid="intake-card-options">
                {savedCards.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold text-[#4A5D6B]">Card on file — charge in one tap</p>
                    {savedCards.map((c) => (
                      <label key={c.id} className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm cursor-pointer ${pmId === c.id ? "border-[#144F75] bg-[#144F75]/5" : "border-[#E5E0D8]"}`} data-testid={`intake-saved-card-${c.id}`}>
                        <input type="radio" name="pm" checked={pmId === c.id} onChange={() => setPmId(c.id)} />
                        <CreditCard size={15} className="text-[#144F75]" /> <span className="capitalize">{c.brand} •••• {c.last4}</span>
                        <span className="text-xs text-[#8FA89B] ml-auto">{String(c.exp_month).padStart(2, "0")}/{String(c.exp_year).slice(-2)}</span>
                      </label>
                    ))}
                    <label className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm cursor-pointer ${pmId === "" ? "border-[#144F75] bg-[#144F75]/5" : "border-[#E5E0D8]"}`} data-testid="intake-use-new-card">
                      <input type="radio" name="pm" checked={pmId === ""} onChange={() => setPmId("")} /> <Plus size={15} className="text-[#144F75]" /> Use a new card
                    </label>
                  </div>
                )}
                {pmId === "" && (
                  <label className="flex items-center gap-2 text-sm text-[#4A5D6B]">
                    <input type="checkbox" checked={saveCard} onChange={(e) => setSaveCard(e.target.checked)} data-testid="intake-save-card" /> Save this card on file for faster deposits next time
                  </label>
                )}
              </div>
            )}
            <div className="mt-4">
              <p className="text-xs font-semibold text-[#1A242B] mb-2">Agreements to email for signing</p>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {agreements.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 text-sm text-[#4A5D6B]" data-testid={`intake-ag-${a.id}`}>
                    <input type="checkbox" checked={isChecked(a)} onChange={() => toggleAg(a.id)} />
                    {a.title}{isRequired(a) && <span className="text-[10px] text-[#D98E73]">required</span>}
                  </label>
                ))}
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="intake-submit" className="eyw-btn w-full mt-5 disabled:opacity-60">{loading && <Loader2 className="animate-spin" size={18} />} Add member &amp; send agreements</button>
          </form>
        )}
      </div>
      {showCard && result?.client_secret && (
        <DepositCardModal
          clientSecret={result.client_secret} publishableKey={result.publishable_key}
          intakeId={result.intake_id} amount={result.deposit?.amount} name={`${f.first_name} ${f.last_name}`.trim()}
          onClose={() => setShowCard(false)}
          onPaid={() => { setShowCard(false); setResult((r) => ({ ...r, deposit: { ...r.deposit, status: "paid" } })); }}
        />
      )}
    </div>
  );
}

function QuickRegisterModal({ onClose, onSaved, onBook }) {
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/pos/register-member", f);
      setDone(data); onSaved();
      toast.success(data.new_member ? "Member registered" : "Existing member linked");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center px-4 bg-black/40 backdrop-blur-sm" data-testid="quick-register-modal">
      <div className="relative w-full max-w-md bg-[#F9F7F3] rounded-3xl shadow-2xl p-8">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[#4A5D6B]"><X size={20} /></button>
        {done ? (
          <div data-testid="quick-register-done">
            <h3 className="font-serif text-2xl text-[#1A242B] mb-1 flex items-center gap-2"><UserCheck size={22} className="text-[#5C8A72]" /> {done.name}</h3>
            <p className="text-sm text-[#4A5D6B] mb-5">Registered as a Discovery Member. No appointment yet — book one now or finish later.</p>
            <div className="flex gap-2">
              <button onClick={onBook} data-testid="quick-register-book" className="eyw-btn flex-1">Book an appointment</button>
              <button onClick={onClose} className="eyw-btn-ghost flex-1">Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h3 className="font-serif text-2xl text-[#1A242B] mb-1">Register member</h3>
            <p className="text-sm text-[#4A5D6B] mb-4">Capture their details now — book an appointment whenever they're ready.</p>
            <div className="grid grid-cols-2 gap-3">
              <input required placeholder="First name" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} data-testid="quick-first" className="eyw-input w-full" />
              <input required placeholder="Last name" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} data-testid="quick-last" className="eyw-input w-full" />
            </div>
            <input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="quick-email" className="eyw-input w-full mt-3" />
            <input placeholder="Phone (optional)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="quick-phone" className="eyw-input w-full mt-3" />
            <button type="submit" disabled={loading} data-testid="quick-submit" className="eyw-btn w-full mt-5 disabled:opacity-60">{loading && <Loader2 className="animate-spin" size={18} />} Register member</button>
          </form>
        )}
      </div>
    </div>
  );
}

export default function POSClients() {
  const [q, setQ] = useState("");
  const [cf, setCf] = useState({ key: "", value: "" });
  const [create, setCreate] = useState(false);
  const [intake, setIntake] = useState(false);
  const [quick, setQuick] = useState(false);
  const [selected, setSelected] = useState(null);
  const qc = useQueryClient();
  const { data: memberFields = [] } = useQuery({
    queryKey: ["cf-member"],
    queryFn: () => api.get("/custom-fields?entity=member").then((r) => r.data),
  });
  const { data: list = [] } = useQuery({
    queryKey: ["pos-clients", q, cf.key, cf.value],
    queryFn: () => api.get(`/pos/clients?q=${encodeURIComponent(q)}${cf.key && cf.value ? `&cf_key=${encodeURIComponent(cf.key)}&cf_value=${encodeURIComponent(cf.value)}` : ""}`).then((r) => r.data),
  });
  const activeField = memberFields.find((f) => f.key === cf.key);

  if (selected) return <ClientProfile id={selected} onBack={() => setSelected(null)} />;

  return (
    <div data-testid="pos-clients">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 eyw-input flex-1 max-w-md">
          <Search size={16} className="text-[#8FA89B]" />
          <input placeholder="Search clients by name, email, phone" value={q} onChange={(e) => setQ(e.target.value)} data-testid="clients-search" className="bg-transparent outline-none flex-1 text-sm" />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setIntake(true)} data-testid="clients-new-member" className="eyw-btn !py-2 text-sm !bg-[#D98E73] hover:!bg-[#c67a60]"><UserCheck size={16} /> New Member + Appt</button>
          <button onClick={() => setQuick(true)} data-testid="clients-register-member" className="eyw-btn-ghost !py-2 text-sm"><UserCheck size={16} /> Register member</button>
          <button onClick={() => setCreate(true)} data-testid="clients-new" className="eyw-btn !py-2 text-sm"><Plus size={16} /> New client</button>
        </div>
      </div>
      {memberFields.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="clients-filter-bar">
          <span className="text-xs text-[#8FA89B]">Filter:</span>
          <select value={cf.key} onChange={(e) => setCf({ key: e.target.value, value: "" })} data-testid="clients-filter-field" className="eyw-input !py-1.5 text-sm">
            <option value="">Any field</option>
            {memberFields.map((f) => <option key={f.id} value={f.key}>{f.label}</option>)}
          </select>
          {activeField && (
            <select value={cf.value} onChange={(e) => setCf({ ...cf, value: e.target.value })} data-testid="clients-filter-value" className="eyw-input !py-1.5 text-sm">
              <option value="">Any value</option>
              {(activeField.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          )}
          {cf.key && <button onClick={() => setCf({ key: "", value: "" })} className="text-xs text-[#144F75] hover:underline">Clear</button>}
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((c) => (
          <button key={c.id} onClick={() => setSelected(c.id)} data-testid={`client-card-${c.id}`} className="eyw-card p-5 text-left hover:border-[#144F75] border border-transparent transition-colors">
            <div className="flex items-center justify-between">
              <span className="text-lg text-[#1A242B]">{c.first_name} {c.last_name}</span>
              {c.checked_in && <span className="text-[10px] bg-[#5C8A72]/15 text-[#5C8A72] px-2 py-0.5 rounded-full">in</span>}
            </div>
            <p className="text-xs text-[#4A5D6B] mt-1">{c.email || "—"}{c.phone && ` · ${c.phone}`}</p>
          </button>
        ))}
        {list.length === 0 && <p className="text-[#4A5D6B] text-sm col-span-full py-10 text-center">No clients found. Create one to get started.</p>}
      </div>
      {create && <CreateClientModal onClose={() => setCreate(false)} onSaved={() => qc.invalidateQueries({ queryKey: ["pos-clients"] })} />}
      {intake && <IntakeModal onClose={() => setIntake(false)} onSaved={() => qc.invalidateQueries({ queryKey: ["pos-clients"] })} />}
      {quick && <QuickRegisterModal onClose={() => setQuick(false)} onSaved={() => qc.invalidateQueries({ queryKey: ["pos-clients"] })} onBook={() => { setQuick(false); setIntake(true); }} />}
    </div>
  );
}
